import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Panel from "../../components/Panel";
import FlowForecastChart from "./FlowForecastChart";
import ForecastDayRow from "./ForecastDayRow";
import ForecastMovementsCard from "./ForecastMovementsCard";
import RefreshIcon from "./RefreshIcon";
import useForecastLots from "../useForecastLots";
import useInventoryForecast from "../useInventoryForecast";
import useVenueDayRollover from "../useVenueDayRollover";
import { hasCapacity } from "../forecastSummary";
import { clampRange, defaultRange, spanDays, today as venueToday } from "../forecastRange";

const useStyles = makeStyles({
  panel: { padding: 20 },
  headerRow: {
    marginBottom: 12,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  title: { margin: 0, marginBottom: 5, fontSize: 20, fontWeight: 600, color: T.fg },
  controls: { marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 },
  controlRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 },
  flex: { flex: 1 },
  controlLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: T.fg,
    marginRight: 4,
    "&:not(:first-child)": { marginLeft: 12 },
  },
  lotSelect: {
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    color: T.fg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
  },
  dateInput: {
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    color: T.fg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "7px 10px",
  },
  dateSep: { color: T.subtleFg, fontSize: 13 },
  refreshBtn: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    color: T.mutedFg,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { background: T.surface, color: T.fg },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  "@keyframes forecastRefreshSpin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  refreshBtnSpinning: {
    "& svg": { animation: "$forecastRefreshSpin 0.9s linear infinite" },
  },
  clampNote: { fontSize: 11, color: T.warning },
  lotsNote: { fontSize: 11, color: T.danger },
});

const GLOBAL_SCOPE = null;
const GLOBAL_VALUE = "__global__";

export default function FlowForecast({ installation }) {
  const s = useStyles();
  const ui = useUi();

  const timezone = installation?.timeZoneName;
  const { lots, loading: lotsLoading, error: lotsError } = useForecastLots(installation?.id);

  const [lotId, setLotId] = useState(GLOBAL_SCOPE);
  const [range, setRange] = useState(() => defaultRange(timezone));
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (timezone) setRange(defaultRange(timezone));
  }, [timezone]);

  // A movement list belongs to the lot and range it was opened from.
  useEffect(() => {
    setSelectedDate(null);
  }, [lotId, range.startDate, range.endDate]);

  /*
   * A window the desk anchored to today follows the clock past midnight
   */
  useVenueDayRollover(timezone, (currentDay, previousDay) => {
    setRange((prev) => {
      if (prev.startDate !== previousDay) return prev;

      // clampRange pushes the end along if the range was a single day.
      const next = clampRange({
        startDate: currentDay,
        endDate: prev.endDate,
        timezone,
        edited: "startDate",
      });
      return { startDate: next.startDate, endDate: next.endDate };
    });
  });

  const { forecast, days, loading, error, refetch } = useInventoryForecast({
    lotId,
    startDate: range.startDate,
    endDate: range.endDate,
  });

  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const refresh = useCallback(async () => {
    if (!refetch) return;
    setRefreshing(true);
    try {
      await refetch();
    } catch (e) {
      // A failed refetch surfaces through the query's own error state, which
      // the chart box already reports; swallowing here just stops it becoming
      // an unhandled rejection.
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, [refetch]);

  const capacity = forecast?.capacity ?? null;
  const selectedDay = selectedDate ? days.find((d) => d.date === selectedDate) : null;
  const movements = selectedDay?.movements ?? [];

  const applyRange = (next, edited) => {
    const result = clampRange({ ...next, timezone, edited });
    setRange({ startDate: result.startDate, endDate: result.endDate });
  };

  const loadFailed = Boolean(error);

  const capped = hasCapacity(capacity);
  const skeletonCount = spanDays(range.startDate, range.endDate);
  const capacityUnit = capped ? ` / ${capacity}` : "";
  const showLoading = loading || (lotsLoading && !lotId);

  return (
    <Panel className={s.panel}>
      <div className={s.headerRow}>
        <div>
          <h2 className={s.title}>Inventory forecast</h2>
          <div className={ui.eyebrow}>
            {lotId === GLOBAL_SCOPE ? "Projected property occupancy" : "Projected lot occupancy"}
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing || showLoading}
          className={cx(s.refreshBtn, refreshing && s.refreshBtnSpinning)}
          aria-label="Refresh forecast"
          title={refreshing ? "Refreshing…" : "Refresh forecast"}
        >
          <RefreshIcon />
        </button>
      </div>

      <div className={s.controls}>
        <div className={s.controlRow}>
          <span className={s.controlLabel}>Lot</span>
          <select
            className={s.lotSelect}
            value={lotId ?? GLOBAL_VALUE}
            onChange={(e) =>
              setLotId(e.target.value === GLOBAL_VALUE ? GLOBAL_SCOPE : e.target.value)
            }
            aria-label="Lot"
          >
            <option value={GLOBAL_VALUE}>Global (whole property)</option>
            {lots.map((lot) => (
              <option key={lot.id} value={lot.id}>
                {lot.name}
                {lot.capacity == null ? " - no capacity set" : ` - ${lot.capacity} spaces`}
              </option>
            ))}
          </select>

          <div className={s.flex} />

          <span className={s.controlLabel}>Dates</span>
          <input
            type="date"
            className={s.dateInput}
            value={range.startDate}
            aria-label="Forecast start date"
            onChange={(e) => applyRange({ ...range, startDate: e.target.value }, "startDate")}
          />
          <span className={s.dateSep}>to</span>
          <input
            type="date"
            className={s.dateInput}
            value={range.endDate}
            aria-label="Forecast end date"
            onChange={(e) => applyRange({ ...range, endDate: e.target.value }, "endDate")}
          />
        </div>

        {lotsError && (
          <div className={s.lotsNote} data-testid="lots-error">
            Could not load the lot list
          </div>
        )}
      </div>

      <FlowForecastChart
        days={days}
        capacity={capacity}
        todayDate={venueToday(forecast?.timezone ?? timezone)}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        loading={showLoading}
        failed={loadFailed}
      />

      {!loadFailed && !showLoading && selectedDay && movements.length > 0 && (
        <ForecastMovementsCard
          date={selectedDay.date}
          movements={movements}
          timezone={forecast?.timezone ?? timezone}
          onClose={() => setSelectedDate(null)}
        />
      )}

      <ForecastDayRow
        days={days}
        capacity={capacity}
        capped={capped}
        capacityUnit={capacityUnit}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        loading={showLoading}
        failed={loadFailed}
        skeletonCount={skeletonCount}
      />
    </Panel>
  );
}
